import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { downloadTalentResume } from '@/services/talentPool.service';
import { downloadBlob, handleDownloadError, sanitizeFilename } from '@/utils/downloadUtils';

/**
 * Downloads a talent's résumé PDF (generated + cached backend-side, rendered in
 * the tenant's fixed language). Mirrors useCandidateDownload's CV flow.
 */
export const useTalentResumeDownload = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('talentPool');

  const [isDownloadingResume, setIsDownloadingResume] = useState(false);

  const downloadResume = useCallback(
    async (profileId: string, talentName: string) => {
      if (isDownloadingResume) return;
      setIsDownloadingResume(true);

      try {
        const token = await getAccessTokenSilently();
        const response = await downloadTalentResume(token, profileId);

        if (!response.data || response.error) {
          throw new Error(response.error || t('toasts.resume_failed'));
        }

        const filename = `resume_${sanitizeFilename(talentName)}.pdf`;
        downloadBlob(
          response.data,
          filename,
          t('toasts.resume_success'),
          t('toasts.resume_failed'),
        );
      } catch (error) {
        handleDownloadError(error, t('toasts.resume_failed'));
      } finally {
        setIsDownloadingResume(false);
      }
    },
    [getAccessTokenSilently, isDownloadingResume, t],
  );

  return { downloadResume, isDownloadingResume };
};
